import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound, Eye, EyeOff, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) { toast.error("أدخل البريد الإلكتروني أو رقم الجوال"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier: identifier.trim() });
      setMaskedEmail(data.email || "");
      setStep(2);
      setCooldown(60);
      toast.success("تم إرسال رمز التحقق إلى بريدك الإلكتروني");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      await api.post("/auth/forgot-password", { identifier: identifier.trim() });
      toast.success("تم إرسال رمز جديد إلى بريدك");
      setCooldown(60);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResending(false); }
  };

  const verifyCode = (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) { toast.error("رمز التحقق مكوّن من 6 أرقام"); return; }
    setStep(3);
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("كلمة المرور يجب أن تكون 6 أحرف على الأقل"); return; }
    if (newPassword !== confirmPassword) { toast.error("كلمتا المرور غير متطابقتين"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        identifier: identifier.trim(), code: code.trim(), new_password: newPassword,
      });
      toast.success("تم تغيير كلمة المرور بنجاح");
      nav("/login", { replace: true });
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      toast.error(msg);
      if (msg.includes("رمز التحقق")) setStep(2);
    } finally { setLoading(false); }
  };

  const inputCls = "w-full rounded-xl bg-[var(--bg-secondary)] px-4 py-3 text-white outline-none";
  const pwWrapInput = "w-full rounded-xl bg-[var(--bg-secondary)] px-4 py-3 pl-12 text-white outline-none";

  return (
    <div className="mx-auto max-w-md px-4 py-14">
      <div className="text-center mb-8">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--amber)] text-[#0B0F17] mb-3"><KeyRound size={28} /></span>
        <h1 className="font-head text-2xl font-black text-white">استعادة كلمة المرور</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {step === 1 && "أدخل بريدك الإلكتروني أو رقم جوالك المسجّل"}
          {step === 2 && (<>أرسلنا رمز تحقق إلى<br /><span className="text-white font-bold" dir="ltr" data-testid="reset-masked-email">{maskedEmail}</span></>)}
          {step === 3 && "أنشئ كلمة مرور جديدة لحسابك"}
        </p>
      </div>

      {step === 1 && (
        <form onSubmit={requestCode} className="ts-card p-6 space-y-4" data-testid="forgot-step-identifier">
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">البريد الإلكتروني أو رقم الجوال</label>
            <input data-testid="forgot-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required
              className={inputCls} placeholder="name@email.com أو 05xxxxxxxx" />
          </div>
          <button data-testid="forgot-submit" disabled={loading} className="btn-amber w-full justify-center disabled:opacity-60">
            {loading ? "جارٍ الإرسال..." : "إرسال رمز التحقق"}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={verifyCode} className="ts-card p-6 space-y-5" data-testid="forgot-step-code">
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">رمز التحقق</label>
            <input data-testid="reset-code-input" value={code} inputMode="numeric" maxLength={6} dir="ltr"
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="______"
              className="w-full rounded-xl bg-[var(--bg-secondary)] px-4 py-3 text-center text-2xl tracking-[0.5em] font-black text-white outline-none" />
          </div>
          <button data-testid="reset-code-submit" className="btn-amber w-full justify-center">متابعة</button>
          <div className="text-center text-sm text-[var(--text-muted)]">
            لم يصلك الرمز؟{" "}
            <button type="button" data-testid="reset-resend-btn" onClick={resend} disabled={cooldown > 0 || resending}
              className={`font-bold ${cooldown > 0 ? "text-[var(--text-subtle)] cursor-not-allowed" : "text-[var(--amber)]"}`}>
              {cooldown > 0 ? `إعادة الإرسال بعد ${cooldown} ثانية` : (resending ? "جارٍ الإرسال..." : "إعادة إرسال الرمز")}
            </button>
          </div>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={resetPassword} className="ts-card p-6 space-y-4" data-testid="forgot-step-password">
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">كلمة المرور الجديدة</label>
            <div className="relative">
              <input data-testid="new-password-input" type={showNew ? "text" : "password"} value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)} required className={pwWrapInput} placeholder="••••••••" />
              <button type="button" data-testid="toggle-new-password" onClick={() => setShowNew((s) => !s)}
                aria-label={showNew ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--amber)] transition-colors">
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">تأكيد كلمة المرور</label>
            <div className="relative">
              <input data-testid="confirm-password-input" type={showConfirm ? "text" : "password"} value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} required className={pwWrapInput} placeholder="••••••••" />
              <button type="button" data-testid="toggle-confirm-password" onClick={() => setShowConfirm((s) => !s)}
                aria-label={showConfirm ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--amber)] transition-colors">
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button data-testid="reset-password-submit" disabled={loading} className="btn-amber w-full justify-center disabled:opacity-60">
            {loading ? "جارٍ الحفظ..." : "تغيير كلمة المرور"}
          </button>
        </form>
      )}

      <p className="text-center text-sm text-[var(--text-muted)] mt-6">
        <Link to="/login" className="text-[var(--amber)] font-bold"><Wrench size={14} className="inline" /> العودة لتسجيل الدخول</Link>
      </p>
    </div>
  );
}
